// AnimateEdit.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import example from "./utils/example.json";
import { DEFAULT_ANIMATION_NAME } from "./Animate";
import { animate } from "../../ai/AnimationAI";
import { supabase } from "../../backend/supabase";
import { useAnimate, AnimationHistoryEntry } from "../../backend/useAnimate";
import { ensureBase64, blobToBase64, uploadBlobToStorage } from "../../backend/storageHelpers";
import { splitVideoFrames } from "./utils/splitVideoFrames";
import { calculateSpriteSheet, SpriteSheetCalculation, FrameDimensions } from "./utils/calculateSpriteSheet";
import { exportAnimation, ExportOptions } from "./utils/exportAnimation";
import { ffmpegService } from "./utils/ffmpegService";
import { cropFramesByConsolidatedTransparency } from "./utils/cropFramesByConsolidatedTransparency";
import { useMediaQuery } from "@mui/material";
import { useAnimateActions } from "./hooks/useAnimateActions";
import { useNavigate } from "react-router-dom";
import { zoomOutImage } from "./utils/zoomOut";
import { squareImage } from "./utils/squareImage";
import { autoGenerateGameArt } from "../../ai/GameArtImageAI";
import { generateName } from "../../ai/NameAI";
import EditRoot from "./layout/EditRoot";

interface AnimateEditProps {
    config: any;
    animationId: string;
    isLoading: boolean;
}

const exampleIndex = 0;

const FRAME_DIMENSIONS: FrameDimensions = {
    width: 480,
    height: 480,
};

const initialSpriteSheetDefaults: SpriteSheetCalculation = {
    rows: 1,
    cols: 1,
    sheetWidth: FRAME_DIMENSIONS.width,
    sheetHeight: FRAME_DIMENSIONS.height,
    gapFrames: 0,
};

const AnimateEdit = ({ config, animationId, isLoading }: AnimateEditProps) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { getAnimationHistory, createHistoryEntry, updateHistoryEntry, remixAnimation, updateAnimation, getAnimation } = useAnimate();
    const { isDoingAction, isAction, startAction, stopAction, currentAction, progress, message } = useAnimateActions();
    const [imageUrl, setImageUrl] = useState<string>('');
    const [userPrompt, setUserPrompt] = useState<string>('');
    const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
    const [animationImages, setAnimationImages] = useState<string[]>([]);
    const isUserTyping = useRef(false);
    const [videoUrl, setVideoUrl] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [numOfFrames, setNumOfFrames] = useState<number>(12);
    const [animationSpeed, setAnimationSpeed] = useState<number>(24);
    const [removeBackground, setRemoveBackground] = useState<boolean>(false);
    const [zoomOut, setZoomOut] = useState<boolean>(false);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState<boolean>(true);
    const [spriteSheetDefaults, setSpriteSheetDefaults] = useState<SpriteSheetCalculation>(initialSpriteSheetDefaults);
    const [frameWidth, setFrameWidth] = useState<number | string>(FRAME_DIMENSIONS.width);
    const [frameHeight, setFrameHeight] = useState<number | string>(FRAME_DIMENSIONS.height);
    const [startFrame, setStartFrame] = useState<number>(0);
    const [endFrame, setEndFrame] = useState<number>(0);
    const [loop, setLoop] = useState<boolean>(false);
    const [hiddenFrames, setHiddenFrames] = useState<number[]>([]);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayIcon, setOverlayIcon] = useState<"play" | "pause">("play");
    const [isProcessingImage, setIsProcessingImage] = useState<boolean>(false);
    const animationRef = useRef<number>();
    const overlayTimeoutRef = useRef<number | null>(null);
    const [animationName, setAnimationName] = useState<string>(DEFAULT_ANIMATION_NAME);
    const nameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isPrivate, setIsPrivate] = useState<boolean>(false);

   const loadAnimationHistory = async () => {
        if (animationId) {
            try {
                const currentAnimation = await getAnimation(animationId);
                if (currentAnimation) {
                    if (currentAnimation.animation_name) {
                        setAnimationName(currentAnimation.animation_name);
                    }
                    // Sync private status from DB
                    setIsPrivate(!!currentAnimation.private);
                }

                const history = await getAnimationHistory(animationId);

                if (history.length > 0) {
                    const latestEntry = history[history.length - 1];
                    setUserPrompt(latestEntry.user_prompt);
                    setImageUrl(latestEntry.reference_image);
                    setAnimationImages(latestEntry.animation_images || []);
                    setVideoUrl(latestEntry.animation_video || '');
                    setCurrentHistoryId(latestEntry.id);

                    // Update frame dimensions based on loaded images
                    if (latestEntry.animation_images && latestEntry.animation_images.length > 0) {
                        const img = new Image();
                        img.onload = () => {
                            setFrameWidth(img.naturalWidth);
                            setFrameHeight(img.naturalHeight);
                            // console.log(`Loaded history: Updated dimensions to ${img.naturalWidth}x${img.naturalHeight}`);
                        };
                        img.src = latestEntry.animation_images[0];
                        
                        // Auto-play when frames are loaded on initial load
                        setIsPlaying(true);
                    }

                    if (isMobile && latestEntry.animation_video) {
                        setIsLeftPanelOpen(false);
                    }
                }
            } catch (error) {
                console.error('Error loading animation history:', error);
            }
        }
    };

    useEffect(() => {
        loadAnimationHistory();
    }, [animationId]);

    useEffect(() => {
        if (!isMobile) {
            setIsLeftPanelOpen(true);
        } else {
            if (videoUrl || animationImages.length > 0) {
                setIsLeftPanelOpen(false);
            } else {
                setIsLeftPanelOpen(true);
            }
        }
    }, [isMobile]);

    useEffect(() => {
        if (config.DEBUG_DISABLE_ANIMATION_AI) {
            const exampleImage = example[exampleIndex].reference_image;
            let url = exampleImage;
            
            // console.log('example reference image', url);
            setImageUrl(url);
        }
    }, []);

    // 1. Calculate sprite sheet defaults whenever the frame range or dimensions change
    useEffect(() => {
        const frameDim: FrameDimensions = {
            width: typeof frameWidth === 'string' ? parseInt(frameWidth) : frameWidth,
            height: typeof frameHeight === 'string' ? parseInt(frameHeight) : frameHeight,
        };
        
        // Filter frames within range that are NOT hidden
        const visibleFramesInRangeCount = animationImages.filter((_, i) => 
            i >= startFrame && i <= endFrame && !hiddenFrames.includes(i)
        ).length;

        // Use the count of visible frames for calculation
        // Since calculateSpriteSheet usually takes start/end indices, we simulate a range based on count
        const defaults = calculateSpriteSheet(0, Math.max(0, visibleFramesInRangeCount - 1), frameDim);
        
        setSpriteSheetDefaults(defaults);
        // console.log('Sprite Sheet Defaults updated:', defaults);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startFrame, endFrame, frameWidth, frameHeight, animationImages.length, hiddenFrames]);

    // 2. Initialize the range when animation images change (e.g., on new generation/load)
    // This effect is separated to prevent the range from resetting during a user's drag action.
    useEffect(() => {
        const totalFrames = animationImages.length;
        const maxIndex = Math.max(0, totalFrames - 1);

        // Check if frames were loaded/generated and the range is not set to full length
        if (totalFrames > 0) {
            // Only initialize if the range is currently [0,0] which indicates it hasn't been set yet
            // if (startFrame === 0 && endFrame === 0) {
                setStartFrame(0);
                setEndFrame(maxIndex);
            // }
        } else {
            // No frames
            if (startFrame !== 0 || endFrame !== 0) {
                setStartFrame(0);
                setEndFrame(0);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animationImages.length]);

    const saveHistoryEntry = useCallback(async (image: string, prompt: string) => {
        if (!image && !prompt) return null; // Cannot save without image or prompt

        try {
            let imageToSave = image;
            // Only convert to base64 if it's not already a remote URL
            if (!image.startsWith('http') && !image.startsWith('https')) {
                imageToSave = await ensureBase64(image);
            }
            
            if (currentHistoryId) {
                // Fetch the current state of the history entry to ensure we don't wipe other fields
                const history = await getAnimationHistory(animationId);
                const existingEntry = history.find(h => h.id === currentHistoryId);

                // Update existing entry while retaining all other existing fields
                const updatedEntry = await updateHistoryEntry(currentHistoryId, {
                    ...existingEntry,
                    user_prompt: prompt,
                    reference_image: imageToSave,
                });
                return updatedEntry;
            } else {
                // Create new entry
                const newEntry = await createHistoryEntry(
                    animationId,
                    prompt,
                    imageToSave,
                    [] // No animation images yet
                );
                setCurrentHistoryId(newEntry.id);
                return newEntry;
            }
        } catch (error) {
            console.error('Error saving history entry:', error);
            return null;
        }
    }, [currentHistoryId, animationId, getAnimationHistory, createHistoryEntry, updateHistoryEntry]);

    const createHistoryEntryFromCurrent = useCallback(async (): Promise<AnimationHistoryEntry | null> => {
        try {
            const history = await getAnimationHistory(animationId);
            if (history.length === 0) {
                console.error('No animation history found');
                return null;
            }
            
            const currentEntry = history[history.length - 1];

            if (!currentEntry) {
                console.error('No history entry found to copy from');
                return null;
            }
            
            // Create a new history entry by copying the current one
            // console.log('Creating new history entry from current...');
            const newEntry = await createHistoryEntry(
                animationId,
                currentEntry.user_prompt,
                currentEntry.reference_image,
                currentEntry.animation_images || [],
                currentEntry.enhanced_prompt || undefined,
                currentEntry.animation_video || undefined,
                currentEntry.reference_image_description || undefined
            );
            
            // console.log('Successfully created new history entry:', newEntry.id);
            setCurrentHistoryId(newEntry.id);
            return newEntry;

        } catch (error) {
            console.error('Error creating new history entry:', error);
            return null;
        }
    }, [animationId, getAnimationHistory, createHistoryEntry]);

    const flashPlayPauseOverlay = (previousIsPlaying: boolean) => {
        // Clear previous timeout if it exists
        if (overlayTimeoutRef.current !== null) {
            clearTimeout(overlayTimeoutRef.current);
            overlayTimeoutRef.current = null;
        }

        // Set the icon to show the PREVIOUS state (before the toggle)
        setOverlayIcon(previousIsPlaying ? "pause" : "play");

        // Show overlay
        setShowOverlay(true);

        // Set a timeout to hide the overlay after 500ms
        overlayTimeoutRef.current = setTimeout(() => {
            setShowOverlay(false);
        }, 500) as unknown as number;
    };

    const handlePlayPauseClick = (e?: React.MouseEvent<HTMLDivElement>) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }

        setIsPlaying(prevIsPlaying => {
            flashPlayPauseOverlay(prevIsPlaying);
            if (!prevIsPlaying && (currentFrameIndex < startFrame || currentFrameIndex > endFrame)) {
                 setCurrentFrameIndex(startFrame);
            }
            return !prevIsPlaying;
        });
    };

    const handleImageChange = async (newImageUrl: string) => {
        // console.log('handleImageChange', newImageUrl);
        
        setImageUrl(newImageUrl);

        if (!newImageUrl) {
            await saveHistoryEntry('', userPrompt);
            return;
        }

        setIsProcessingImage(true);
        const savedEntry = await saveHistoryEntry(newImageUrl, userPrompt);
        
        if (savedEntry) {
            setCurrentHistoryId(savedEntry.id);
            // console.log('Saved entry:', savedEntry);

            try {
                // Automatically square the image after saving the initial upload
                setIsProcessingImage(true);
                const squaredEntry = await squareImage(
                    savedEntry,
                    animationId,
                    createHistoryEntry
                );

                if (squaredEntry) {
                    // console.log('Squared entry:', squaredEntry);
                    setImageUrl(squaredEntry.reference_image);
                    setCurrentHistoryId(squaredEntry.id);
                }
            } catch (error) {
                console.error('Error during auto-squaring:', error);
            } finally {
                setIsProcessingImage(false);
            }
        }
    };
    
    const handleImageError = (error: string) => {
        console.error(error);
        setError(error);
    };

    const handleUserPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        isUserTyping.current = true;
        const currentValue = e.target.value;
        setUserPrompt(currentValue);
    };

    // Debounce save for user prompt
    useEffect(() => {
        if (!isUserTyping.current) return;

        const timeoutId = setTimeout(() => {
            if (imageUrl && userPrompt) {
                saveHistoryEntry(imageUrl, userPrompt);
                isUserTyping.current = false;
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [userPrompt, imageUrl, currentHistoryId, saveHistoryEntry]);

    // Automatically process video frame splitting when we have a video but no frames
    useEffect(() => {
        const shouldProcessFrames = 
            videoUrl && 
            (!animationImages || animationImages.length === 0) &&
            !isDoingAction; // Only if no other action is running

        if (shouldProcessFrames) {
            // console.log('Video detected without frames, processing split...');
            processSplitFrames(videoUrl);
        }
    }, [videoUrl, animationImages, isDoingAction]);

    const handleNumFramesChange = (newValue: number) => {
        setNumOfFrames(newValue);
    };

    const handleAnimationSpeedChange = (newValue: number) => {
        setAnimationSpeed(newValue);
    };

    const handleRemoveBackgroundChange = (newValue: boolean) => {
        setRemoveBackground(newValue);
    };

    const handleZoomOutChange = (newValue: boolean) => {
        setZoomOut(newValue);
    };
    
    const handleFrameWidthChange = (newValue: string | number) => {
        setFrameWidth(newValue);
    };

    const handleFrameHeightChange = (newValue: string | number) => {
        setFrameHeight(newValue);
    };

    const handleRangeChange = (newStart: number, newEnd: number) => {
        setStartFrame(newStart);
        setEndFrame(newEnd);
        // console.log(`Export range updated: ${newStart} to ${newEnd}`);

        // When range changes, ensure currentFrame is within the new range
        setCurrentFrameIndex(prevIndex => 
            Math.min(Math.max(prevIndex, newStart), newEnd)
        );
        
        // Ensure animation is paused when scrubbing range
        const isPlayingBeforeToggle = isPlaying;
        setIsPlaying(false);
        flashPlayPauseOverlay(isPlayingBeforeToggle); 
    };

    const handleLoopChange = (newValue: boolean) => {
        setLoop(newValue);
    };

    const processSplitFrames = async (videoUrl: string, asSubAction = false, discardFirstFrame = false) => {
        try {
            if (!asSubAction) {
                startAction('split_frames');
            }
            // console.log('Starting video frame splitting...');

            let newEntry;

            if (config.VIDEO_TRANSFORM_APPROACH === 'cloudflare') {
                // console.log('Using Cloudflare for splitting frames...');
                
                const result = await ffmpegService.splitFrames({
                    videoUrl,
                    animationId,
                    fps: animationSpeed,
                    discardFirstFrame
                });
                
                newEntry = result.historyEntry;
                // console.log('Backend split success:', newEntry);

            } else {
                // console.log('Using client-side FFmpeg WASM for splitting frames...');
                // Client-side FFmpeg WASM
                // Fetch the video as a blob
                const response = await fetch(videoUrl);
                const videoBlob = await response.blob();

                // console.log('Video received: ', videoUrl);

                // Split the video into frames
                const frameBlobs = await splitVideoFrames(videoBlob, { discardFirstFrame });

                // console.log(`Split video into ${frameBlobs.length} frames`);

                // Convert all frame blobs to base64
                const frameBase64s = await Promise.all(
                    frameBlobs.map(blob => blobToBase64(blob))
                );

                // Get the current history entry to copy it
                const history = await getAnimationHistory(animationId);
                // Actually, let's reuse the existing logic structure but cleaned up
                
                let currentEntryPrompt = userPrompt;
                let currentEntryRefImage = imageUrl;
                let currentEntryEnhancedPrompt = undefined;
                let currentEntryRefDesc = undefined;

                if (history.length > 0) {
                    const currentEntry = history[history.length - 1];
                    currentEntryPrompt = currentEntry.user_prompt;
                    currentEntryRefImage = currentEntry.reference_image;
                    currentEntryEnhancedPrompt = currentEntry.enhanced_prompt;
                    currentEntryRefDesc = currentEntry.reference_image_description;
                }

                // Create a new history entry with the frames
                // console.log('Creating new history entry with frames...');
                newEntry = await createHistoryEntry(
                    animationId,
                    currentEntryPrompt,
                    currentEntryRefImage,
                    frameBase64s, // Pass the frame images
                    currentEntryEnhancedPrompt,
                    videoUrl, // Keep the video URL
                    currentEntryRefDesc
                );

                // console.log('Successfully created new history entry with frames:', newEntry.id);
            }

            // Frontend State Update
            if (newEntry) {
                const newImages = newEntry.animation_images;
                setAnimationImages(newImages);
                setCurrentHistoryId(newEntry.id);

                // Reset the range to the full sequence
                if (newImages.length > 0) {
                    setStartFrame(0);
                    setEndFrame(newImages.length - 1);
                    setHiddenFrames([]);
                    // console.log(`Range reset to full: 0 to ${newImages.length - 1}`);
                }
                // console.log('Frontend frames set:', newImages);
            }

        } catch (error) {
            console.error('Error processing split frames:', error);
            setError('Failed to split video into frames. Please try again.');
        } finally {
            if (!asSubAction) {
                stopAction();
            }
        }
    };

    const autoGenerateAnimationName = async (prompt: string) => {
        try {
            const currentAnimation = await getAnimation(animationId);
            if (currentAnimation && (currentAnimation.animation_name === DEFAULT_ANIMATION_NAME || !currentAnimation.animation_name)) {
                // console.log('Current name is default, generating new name...');
                const newName = await generateName({
                    prompt: prompt,
                    type: 'animation'
                });
                
                if (newName) {
                    const updatedAnimation = await updateAnimation(animationId, { animation_name: newName });
                    // console.log('Saved to DB: ', updatedAnimation);

                    // Update local state so the UI reflects the change immediately
                    setAnimationName(newName);
                    // console.log('Updated animation name to:', newName);
                }
            }
        } catch (error) {
            console.error('Error auto-generating animation name:', error);
        }
    };

    const handleAnimate = async () => {
        // console.log('Animate button clicked');
        if (!imageUrl) {
            setError('Please upload an image first');
            return;
        }
        if (!userPrompt.trim()) {
            setError('Please enter animation instructions');
            return;
        }
        
        startAction('animate');
        setError('');

        // Try to generate a name if it's currently default
        autoGenerateAnimationName(userPrompt);

        let currentImage = imageUrl;
        let activeHistoryId = currentHistoryId;

        // If zoomOut is true, process the image before starting animation
        if (zoomOut) {
            try {
                setIsProcessingImage(true);
                // console.log('Zoom out enabled, processing image...');
                const history = await getAnimationHistory(animationId);
                const currentEntry = history[history.length - 1];
                
                if (currentEntry) {
                    const zoomedEntry = await zoomOutImage(
                        currentEntry,
                        animationId,
                        createHistoryEntry
                    );

                    if (zoomedEntry) {
                        currentImage = zoomedEntry.reference_image;
                        activeHistoryId = zoomedEntry.id;
                        setImageUrl(currentImage);
                        setCurrentHistoryId(activeHistoryId);
                        setZoomOut(false);
                        // console.log('Zoom out complete, new reference image set.');
                    }
                }
            } catch (zoomError) {
                console.error('Error during zoom out process:', zoomError);
                // Continue with original image if zoom out fails
            } finally {
                setIsProcessingImage(false);
            }
        }
        
        const image = currentImage;
        // console.log('Generate animation with:', { image, userPrompt, removeBackground, loop });

        if (config.DEBUG_DISABLE_ANIMATION_AI) {
            // console.log('DEBUG_DISABLE_ANIMATION_AI is set, using example animation.');
            
            const exampleVideoUrl = example[exampleIndex].animation_video;

            if (exampleVideoUrl) {
                // Simulate the delay of the AI request
                await new Promise(resolve => setTimeout(resolve, 5000));
                
                // Set the video URL from the example
                setVideoUrl(exampleVideoUrl);
                // console.log('Example video received.');

                // If we have a video, split it into frames
                if (exampleVideoUrl) {
                    // console.log('Video received, splitting into frames...');
                    // processSplitFrames will call setIsGenerating(false) when complete
                    // Important: processSplitFrames uses 'split_frames' action, which overwrites 'animate'.
                    // This is fine as the visual effect (loading) is the same, but semantically a shift.
                    await processSplitFrames(exampleVideoUrl, true, true);
                    stopAction();
                } else {
                    // This case should be covered by the outer check but kept for completeness
                    stopAction();
                }
            } else {
                console.error('Example data missing animation video for debug mode.');
                stopAction();
            }
            
        } else {
            try {
                const result = await animate({
                    request_type: 'generate-animation',
                    animation_id: animationId,
                    reference_image: image,
                    user_prompt: userPrompt,
                    animation_history_id: activeHistoryId || undefined,
                    loop: loop,
                });
                // console.log('Animation result:', result);
                
                // Update state by fetching latest history from DB
                await loadAnimationHistory();
                
                // Get the latest video URL from the result or from the loaded history
                const latestHistory = await getAnimationHistory(animationId);
                if (latestHistory.length > 0) {
                    const latestEntry = latestHistory[latestHistory.length - 1];
                    const latestVideoUrl = latestEntry.animation_video;
                    
                    // If we have a video, split it into frames
                    if (latestVideoUrl) {
                        // Call processSplitFrames to handle the splitting and final setIsGenerating(false)
                        setVideoUrl(latestVideoUrl);
                        await processSplitFrames(latestVideoUrl, true, true);
                        stopAction();
                    } else {
                        // If no video URL is present in the history, stop generating.
                        stopAction();
                    }
                } else {
                    // If no history is found, stop generating.
                    stopAction();
                }
                
            } catch (error) {
                console.error('Error during animation generation:', error);
                setError('Failed to generate animation. Please try again.');
                stopAction();
            }
        }
    };
    
    const handleRemoveVideoBackground = async () => {
        // console.log('Remove Background button clicked');
        
        if (!animationId) {
            setError('Missing animation ID.');
            return;
        }

        if (!animationImages || animationImages.length === 0) {
            setError('Please generate an animation first to remove its background.');
            return;
        }

        startAction('remove_background');
        setError('');
        
        try {
            // 1. Create a new history entry by copying the current one
            const newHistoryEntry = await createHistoryEntryFromCurrent();
            if (!newHistoryEntry) {
                throw new Error('Failed to create new history entry for background removal.');
            }
            const newHistoryId = newHistoryEntry.id;
            // console.log('New history entry created for background removal:', newHistoryId);
            
            // 2. Export the current frames as a WebM video
            // console.log('Exporting frames as WebM for background removal...');
            
            // Slice images to the current range and filter out hidden frames
            const framesToExport = animationImages.filter((_, i) => 
                i >= startFrame && i <= endFrame && !hiddenFrames.includes(i)
            );

            let uploadedVideoUrl: string;

            if (config.VIDEO_TRANSFORM_APPROACH === 'cloudflare') {
                 // console.log('Using Cloudflare for webm export...');

                 const { url } = await ffmpegService.exportAnimation({
                    images: framesToExport,
                    format: 'webm',
                    fps: animationSpeed,
                    animationId: animationId
                });
                uploadedVideoUrl = url;
            } else {
                // console.log('Using frontend for webm export...');
                const exportOptions: ExportOptions = {
                    format: 'webm',
                    fps: animationSpeed, // Use current animation speed
                };
                const videoBlob = await exportAnimation(framesToExport, exportOptions);

                // 3. Upload the exported video to storage
                // console.log('Video exported and uploading to storage...');
                
                const STORAGE_FOLDER = 'animation-assets';
                uploadedVideoUrl = await uploadBlobToStorage(
                    supabase,
                    videoBlob,
                    'video-remove-bg', // Prefix for file name
                    STORAGE_FOLDER, 
                    'video/webm' // Content Type
                );
            }

            // console.log('Video uploaded successfully to:', uploadedVideoUrl);
            
            if (config.DEBUG_DISABLE_ANIMATION_AI) {
                // console.log('DEBUG_DISABLE_ANIMATION_AI is set, mock background removal.');
                
                // The exported and uploaded video URL is used as the result of the mock AI processing.
                // This tests the video-to-frame splitting on the file we just created and uploaded.
                
                // Simulate the AI processing delay
                await new Promise(resolve => setTimeout(resolve, 5000));

                // Update the new history entry with the URL of the UPLOADED video.
                await updateHistoryEntry(newHistoryId, {
                    animation_video: uploadedVideoUrl,
                    animation_images: [], // Ensure images are clear for re-split
                });
                
                // Update state to set the new history ID and video URL
                setCurrentHistoryId(newHistoryId);
                setVideoUrl(uploadedVideoUrl);

                // console.log('Mocked background removal complete, calling processSplitFrames.');
                // Calling processSplitFrames here to get the frames and set setIsGenerating(false)
                await processSplitFrames(uploadedVideoUrl, true);
            } else {
                // console.log('Removing video background...');
                
                // 4. Update the new history entry with the URL of the video to be processed
                await updateHistoryEntry(newHistoryId, {
                    animation_video: uploadedVideoUrl,
                });
                
                // Update state with the new history ID and video URL (which is the input video for the worker)
                setCurrentHistoryId(newHistoryId);
                setVideoUrl(uploadedVideoUrl);

                // 5. Send request to remove video background
                // console.log('Sending request to remove video background...');
                const result = await animate({
                    request_type: 'remove-video-background',
                    animation_id: animationId,
                    reference_video: uploadedVideoUrl,
                    animation_history_id: newHistoryId, // Use the new history ID
                });

                // console.log('Remove Background result:', result);

                // 6. Update state by fetching latest history from DB
                await loadAnimationHistory();

                // 7. Re-split frames from the new video URL in the history
                const latestHistory = await getAnimationHistory(animationId);
                if (latestHistory.length > 0) {
                    const latestEntry = latestHistory[latestHistory.length - 1];
                    const latestVideoUrl = latestEntry.animation_video;
                    
                    if (latestVideoUrl) {
                        // console.log('New video with transparent background received, splitting into frames...');

                        // Call processSplitFrames to handle the splitting and final setIsGenerating(false)
                        setVideoUrl(latestVideoUrl);
                        await processSplitFrames(latestVideoUrl, true);
                    }
                }
            }

            // console.log('Background removal complete. Stopping action.');
            stopAction();

        } catch (error) {
            console.error('Error during background removal:', error);
            setError('Failed to remove background. Please try again.');
            stopAction();
        }
    };

    const handleCropTransparentPixels = async () => {
        if (!animationImages || animationImages.length === 0) {
            setError('No images to crop.');
            return;
        }

        startAction('crop_transparent');
        setError('');

        try {
            // console.log('Loading frames for transparency analysis...');
            
            // Filter images to the current range and filter out hidden frames
            const visibleFramesInRange = animationImages.filter((_, i) => 
                i >= startFrame && i <= endFrame && !hiddenFrames.includes(i)
            );

            // 1. Load images into off-screen elements to analyze pixel data
            // We set crossOrigin to "anonymous" to prevent Tainted Canvas errors
            const framePromises = visibleFramesInRange.map((src, idx) => {
                return new Promise<HTMLImageElement>((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                        // console.log(`Loaded image ${idx} for crop. Size: ${img.naturalWidth}x${img.naturalHeight}`);
                        resolve(img);
                    };
                    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
                    img.src = src;
                });
            });

            const imageElements = await Promise.all(framePromises);

            // 2. Perform the consolidated cropping using off-screen canvases inside the utility
            const croppedCanvases = cropFramesByConsolidatedTransparency(imageElements);

            if (!croppedCanvases || croppedCanvases.length === 0) {
                throw new Error("Cropping failed or returned no results.");
            }

            // console.log(`Received ${croppedCanvases.length} cropped canvases. Converting to data URLs...`);

            // 3. Convert off-screen canvases back to base64 strings for storage and state
            const croppedBase64s = croppedCanvases.map(canvas => canvas.toDataURL('image/png'));

            // 4. Update frame dimensions based on the new cropped size
            const newWidth = croppedCanvases[0].width;
            const newHeight = croppedCanvases[0].height;
            setFrameWidth(newWidth);
            setFrameHeight(newHeight);
            // console.log(`Updated frame dimensions to: ${newWidth}x${newHeight}`);

            // 5. Create a new history entry for the cropped version to allow undo
            const newHistoryEntry = await createHistoryEntryFromCurrent();
            if (!newHistoryEntry) {
                throw new Error("Failed to create history entry for cropping.");
            }

            await updateHistoryEntry(newHistoryEntry.id, {
                animation_images: croppedBase64s,
            });

            // 6. Update local state
            setAnimationImages(croppedBase64s);
            setCurrentHistoryId(newHistoryEntry.id);
            setStartFrame(0);
            setEndFrame(croppedBase64s.length - 1);
            setHiddenFrames([]);
            // console.log('Successfully cropped transparency and updated history.');

        } catch (err: any) {
            console.error('Error cropping transparency:', err);
            setError('Failed to crop transparent pixels. Check console for details.');
        } finally {
            stopAction();
        }
    };

    const handleExport = async (formatId: string, settings: any) => {
        if (!animationImages || animationImages.length === 0) {
            console.error('No images to export');
            return;
        }

        startAction('export');
        try {
            // console.log(`Exporting with ID: ${formatId} and settings:`, settings);
            
            // Filter frames within range that are NOT hidden
            const framesToExport = animationImages.filter((_, i) => 
                i >= startFrame && i <= endFrame && !hiddenFrames.includes(i)
            );

            // Helper to get export from backend
            const blobFromBackend = async (format: string, fpsVal: number, extraSettings: any = {}) => {
                const { url } = await ffmpegService.exportAnimation({
                    images: framesToExport,
                    format: format,
                    fps: fpsVal,
                    animationId: animationId,
                    ...extraSettings
                });
                
                // The backend returns a URL to the file in storage
                const downloadUrl = url;
                const res = await fetch(downloadUrl);
                return await res.blob();
            };

            const historyExportTask = async () => {
                const isFullRange = startFrame === 0 && endFrame === animationImages.length - 1;
                
                if (isFullRange && hiddenFrames.length === 0) {
                    return;
                }

                try {
                    // console.log('Starting parallel background video export...');
                    const videoOptions: ExportOptions = {
                        format: 'webm',
                        fps: animationSpeed,
                    };
                    
                    let historyVideoBlob: Blob;
                    let uploadedVideoUrl: string;

                    if (config.VIDEO_TRANSFORM_APPROACH === 'cloudflare') {
                        // console.log('Using cloudflare for history export...');
                        const { url } = await ffmpegService.exportAnimation({
                            images: framesToExport,
                            format: 'webm',
                            fps: animationSpeed,
                            animationId: animationId
                        }); 
                        uploadedVideoUrl = url;

                    } else {
                         historyVideoBlob = await exportAnimation(framesToExport, videoOptions);
                         const STORAGE_FOLDER = 'animation-assets';
                         uploadedVideoUrl = await uploadBlobToStorage(
                            supabase,
                            historyVideoBlob,
                            'exported-video-bg', 
                            STORAGE_FOLDER, 
                            'video/webm'
                        );
                    }

                    // console.log('Re-splitting frames from exported video...');
                    await processSplitFrames(uploadedVideoUrl, true);
                } catch (historyError) {
                    console.error('Parallel background export failed:', historyError);
                }
            };

            const userExportTask = async () => {
                let exportFormat: 'gif' | 'mp4' | 'png' | 'zip' | 'webm';
                let startDownload = true;

                switch (formatId) {
                    case 'gif':
                        exportFormat = 'gif';
                        break;
                    case 'video':
                        exportFormat = 'mp4';
                        break;
                    case 'webm':
                        exportFormat = 'webm';
                        break;
                    case 'sprite_sheet':
                        exportFormat = 'png';
                        // Backend uses 'sprite_sheet' as format for export logic, but file extension is png
                        break;
                    case 'pngs':
                        exportFormat = 'zip';
                        break;
                    default:
                        console.warn(`Unknown export format ID: ${formatId}. Defaulting to png.`);
                        exportFormat = 'png';
                        break;
                }

                const options: ExportOptions = {
                    format: exportFormat,
                    fps: animationSpeed,
                    rows: settings?.rows,
                    cols: settings?.cols,
                    sheetWidth: settings?.sheetWidth,
                    sheetHeight: settings?.sheetHeight,
                };
                
                let blob: Blob;

                // Check for 'zip' explicitly to ensure it always runs on the client
                if (config.VIDEO_TRANSFORM_APPROACH === 'cloudflare' && exportFormat !== 'zip') {
                    // console.log('Using cloudflare for user export...');
                    if (formatId === 'sprite_sheet') {
                         blob = await blobFromBackend('sprite_sheet', animationSpeed, {
                             rows: settings?.rows,
                             cols: settings?.cols,
                             sheetWidth: settings?.sheetWidth,
                             sheetHeight: settings?.sheetHeight
                         });
                    } else {
                         blob = await blobFromBackend(exportFormat, animationSpeed);
                    }
                } else {
                   // Client-side export (FFmpeg WASM or JSZip)
                   blob = await exportAnimation(framesToExport, options);
                }
                
                return { blob, exportFormat };
            };

            const [userExportResult] = await Promise.all([
                userExportTask(),
                historyExportTask()
            ]);

            const { blob, exportFormat } = userExportResult;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            const name = animationName.replace(/\s+/g, '_');
            a.download = `${name}.${exportFormat}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            await loadAnimationHistory();
            
        } catch (error) {
            console.error('User export failed:', error);
            setError('Export failed. Please try again.');
        } finally {
            stopAction();
        }
    };

    const handleDuplicateFrame = (frameBase64: string, index: number) => {
        const newAnimationImages = [...animationImages];
        // Insert the duplicate right after the current index
        newAnimationImages.splice(index + 1, 0, frameBase64);
        
        // Update images
        setAnimationImages(newAnimationImages);

        // Pause animation and show visual feedback
        const isPlayingBeforeToggle = isPlaying;
        setIsPlaying(false);
        flashPlayPauseOverlay(isPlayingBeforeToggle); 
        
        // Move the local current frame index to the position of the duplicated frame
        setCurrentFrameIndex(index + 1);

        // Update range to include the new frame if the duplication happened within or at the end of current range
        if (index <= endFrame) {
            setEndFrame(prev => prev + 1);
        }
    };

    const handleDuplicateCurrentFrame = () => {
        if (animationImages.length > 0) {
            const currentFrameBase64 = animationImages[currentFrameIndex];
            handleDuplicateFrame(currentFrameBase64, currentFrameIndex);
        }
    };
    
    const handleReorder = (newOrder: string[]) => {
        // Update the image array (source of truth)
        setAnimationImages(newOrder);

        // Ensure animation is paused after reordering
        const isPlayingBeforeToggle = isPlaying;
        setIsPlaying(false);
        // Show overlay to indicate the animation is paused and frame changed
        flashPlayPauseOverlay(isPlayingBeforeToggle);
    };

    const handleToggleFrameVisibility = (index: number) => {
        const isPlayingBeforeToggle = isPlaying;
        // setIsPlaying(false);
        flashPlayPauseOverlay(isPlayingBeforeToggle);
        
        setHiddenFrames(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index) 
                : [...prev, index]
        );
    };

    const handleSeek = (value: number) => {
        const isPlayingBeforeToggle = isPlaying;
        setIsPlaying(false); 
        setCurrentFrameIndex(value);
        flashPlayPauseOverlay(isPlayingBeforeToggle); 
    };

    const onFrameClick = (index: number) => {
        if (isPlaying) {
             flashPlayPauseOverlay(true); 
        } else {
             flashPlayPauseOverlay(false); 
        }
        setIsPlaying(false);
        setCurrentFrameIndex(index);
    };

    const handleGenerateImage = async (prompt: string) => {
        if (!prompt) {
            return;
        }

        // console.log('Generating image for asset:', prompt);
        setIsProcessingImage(true);
        setError('');

        try {
            const result = await autoGenerateGameArt({
                req: {
                    id: animationId,
                    asset_name: animationName,
                    asset_description: prompt
                },
                config: config
            });
            // console.log('Image generation result:', result);

            if (result && result.url) {
                await handleImageChange(result.url);
                // console.log('Image saved successfully:', result.url);
            }
        } catch (error) {
            console.error('Error generating image:', error);
            setError('Failed to generate image. Please try again.');
        } finally {
            setIsProcessingImage(false);
        }
    };

    const handleRemix = async () => {
        if (!animationId) return;
        
        // startAction('remix');
        try {
            // remixAnimation handles creating the new DB entry and copying the latest history
            const newAnimation = await remixAnimation(animationId, currentHistoryId || undefined);
            
            if (newAnimation && newAnimation.id) {
                // Navigate the user to the new animation's edit page
                navigate(`/animate/${newAnimation.id}`);
            }
        } catch (error) {
            console.error('Error remixing animation:', error);
            setError('Failed to remix animation. Please try again.');
        } finally {
            // stopAction();
        }
    };

    const handleAnimationNameChange = (newName: string) => {
        setAnimationName(newName);

        // Clear existing timeout
        if (nameTimeoutRef.current) {
            clearTimeout(nameTimeoutRef.current);
        }

        // Debounce the database update
        nameTimeoutRef.current = setTimeout(async () => {
            try {
                await updateAnimation(animationId, { animation_name: newName });
                // console.log('Animation name persisted to DB:', newName);
            } catch (error) {
                console.error('Error persisting animation name:', error);
            }
        }, 1000);
    };

    const handleVisibilityChange = async (newVisibility: 'public' | 'private') => {
        const privateValue = newVisibility === 'private';
        setIsPrivate(privateValue);
        
        try {
            await updateAnimation(animationId, { private: privateValue });
        } catch (error) {
            console.error('Error updating visibility:', error);
        }
    };

    // Animation loop effect
    useEffect(() => {
        // Clear any existing animation frame
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        
        // Use animationImages (now the canonical source) for animation playback
        if (animationImages && animationImages.length > 0 && isPlaying) {
            const frameInterval = 1000 / animationSpeed; // Convert FPS to milliseconds
            let lastTimestamp = 0;
            const playStart = startFrame; // Use start frame PROP
            const playEnd = endFrame; // Use end frame PROP
            
            // Set initial frame to startFrame if it's currently outside the range
            setCurrentFrameIndex(prevIndex => {
                if (prevIndex < playStart || prevIndex > playEnd) {
                    return playStart;
                }
                return prevIndex;
            });
            
            const updateFrame = (timestamp: number) => {
                if (!lastTimestamp || timestamp - lastTimestamp >= frameInterval) {
                    setCurrentFrameIndex(prevIndex => {
                        let nextIndex = prevIndex + 1;
                        
                        // If we hit the end of the range, loop back to the start
                        if (nextIndex > playEnd) {
                            nextIndex = playStart;
                        }

                        // Skip manually hidden frames within the range
                        while (hiddenFrames.includes(nextIndex) && nextIndex <= playEnd) {
                            nextIndex++;
                            if (nextIndex > playEnd) {
                                nextIndex = playStart;
                            }
                            // Infinite loop protection: if all frames in range are hidden
                            if (nextIndex === (prevIndex + 1 > playEnd ? playStart : prevIndex + 1)) {
                                break;
                            }
                        }

                        return nextIndex;
                    });
                    lastTimestamp = timestamp;
                }
                animationRef.current = requestAnimationFrame(updateFrame);
            };
            
            animationRef.current = requestAnimationFrame(updateFrame);
        };
        
        // Cleanup function
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [animationImages, animationSpeed, isPlaying, startFrame, endFrame, hiddenFrames]);

    // Track previous action to detect completion
    const prevActionRef = useRef(currentAction);
    useEffect(() => {
        const prevAction = prevActionRef.current;
        
        // If we transitioned from an action (non-null) to null (finished/stopped)
        if (prevAction !== null && currentAction === null) {
            if (animationImages.length > 0 && !isPlaying) {
                setIsPlaying(true);
            }
        }
        
        prevActionRef.current = currentAction;
    }, [currentAction, animationImages, isPlaying]);

    // Initial load auto-play: ensure it plays if images exist and no action is currently running
    useEffect(() => {
        if (animationImages.length > 0 && !isDoingAction) {
             setIsPlaying(true);
        }
    }, [animationImages.length, isDoingAction]);
   
   // Handle when images are CLEARED
   useEffect(() => {
        if (animationImages.length === 0) {
            setIsPlaying(false);
        }
   }, [animationImages.length]);

    // Keyboard controls
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            if (animationImages.length === 0) return;

            const isPlayingBeforeToggle = isPlaying;
            setIsPlaying(false);
            flashPlayPauseOverlay(isPlayingBeforeToggle); 
            
            const numFramesInOrder = animationImages.length;

            if (event.key === 'ArrowLeft') {
                setCurrentFrameIndex(prevIndex => {
                    let newIndex = prevIndex - 1;
                    if (newIndex < startFrame) {
                        return endFrame;
                    } 
                    if (newIndex < 0) {
                        return numFramesInOrder - 1;
                    }
                    return newIndex;
                });
            } else if (event.key === 'ArrowRight') {
                setCurrentFrameIndex(prevIndex => {
                    let newIndex = prevIndex + 1;
                    if (newIndex > endFrame) {
                        return startFrame;
                    }
                    if (newIndex >= numFramesInOrder) {
                        return 0;
                    }
                    return newIndex;
                });
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [animationImages, isPlaying, startFrame, endFrame]); 
    
    useEffect(() => {
        if (animationImages) { 
            const newMaxIndex = Math.max(0, animationImages.length - 1);
            
            setCurrentFrameIndex(prevIndex => {
                const newIndex = Math.min(prevIndex, newMaxIndex);
                return Math.max(0, newIndex);
            });
        }
    }, [animationImages]);

    // Calculate display values based on visible frames within range
    const visibleFrames = animationImages.filter((_, i) => i >= startFrame && i <= endFrame && !hiddenFrames.includes(i));
    const displayMaxFrames = animationImages.length > 0 ? visibleFrames.length : numOfFrames;

    // Determine current "relative" frame number (position among visible frames)
    let currentRelativeFrameNumber = 0;
    if (animationImages.length > 0) {
        if (hiddenFrames.includes(currentFrameIndex) || currentFrameIndex < startFrame || currentFrameIndex > endFrame) {
            currentRelativeFrameNumber = 0;
        } else {
            // Find the index of the current absolute index within the visible subset
            const indexInVisible = animationImages
                .map((_, i) => i)
                .filter(i => i >= startFrame && i <= endFrame && !hiddenFrames.includes(i))
                .indexOf(currentFrameIndex);
            
            currentRelativeFrameNumber = indexInVisible !== -1 ? indexInVisible + 1 : 0;
        }
    }

    return (
        <EditRoot
            config={config}
            isLoading={isLoading}
            animationId={animationId}
            initialName={animationName}
            spriteSheetDefaults={spriteSheetDefaults}
            animationImages={animationImages}
            currentAction={currentAction}
            imageUrl={imageUrl}
            userPrompt={userPrompt}
            handleImageChange={handleImageChange}
            handleImageError={handleImageError}
            handleUserPromptChange={handleUserPromptChange}
            handleAnimate={handleAnimate}
            removeBackground={removeBackground}
            handleRemoveBackgroundChange={handleRemoveBackgroundChange}
            isLeftPanelOpen={isLeftPanelOpen}
            setIsLeftPanelOpen={setIsLeftPanelOpen}
            animationSpeed={animationSpeed}
            handleAnimationSpeedChange={handleAnimationSpeedChange}
            frameWidth={frameWidth}
            frameHeight={frameHeight}
            handleFrameWidthChange={handleFrameWidthChange}
            handleFrameHeightChange={handleFrameHeightChange}
            loop={loop}
            handleLoopChange={handleLoopChange}
            isMobile={isMobile}
            maxFrames={displayMaxFrames}
            handleNumFramesChange={handleNumFramesChange}
            handleDuplicateCurrentFrame={handleDuplicateCurrentFrame}
            handleRemoveVideoBackground={handleRemoveVideoBackground}
            handleCropTransparentPixels={handleCropTransparentPixels}
            handleReorder={handleReorder}
            startFrame={startFrame}
            endFrame={endFrame}
            handleRangeChange={handleRangeChange}
            hiddenFrames={hiddenFrames}
            handleToggleFrameVisibility={() => handleToggleFrameVisibility(currentFrameIndex)}
            currentFrameIndex={currentFrameIndex}
            currentFrameNumber={currentRelativeFrameNumber}
            displayMaxFrames={displayMaxFrames}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            showOverlay={showOverlay}
            overlayIcon={overlayIcon}
            handlePlayPauseClick={handlePlayPauseClick}
            handleSeek={handleSeek}
            onFrameClick={onFrameClick}
            handleExport={handleExport}
            progress={progress}
            message={message}
            handleRemix={handleRemix}
            zoomOut={zoomOut}
            handleZoomOutChange={handleZoomOutChange}
            isProcessingImage={isProcessingImage}
            handleGenerateImage={handleGenerateImage}
            handleAnimationNameChange={handleAnimationNameChange}
            visibility={isPrivate ? 'private' : 'public'}
            onVisibilityChange={handleVisibilityChange}
        />
    );
};

export default AnimateEdit;